'use strict';
import { fh_add, fh_mul, fh_polygon } from '@acenv/fh-3d-viewer';
//***********************************************************************************
//***********************************************************************************
//**** Build nomenclatures
//***********************************************************************************
//***********************************************************************************
import { extension_instance } from '../extension/cn_extension';
import { BC_MATERIAL_LIST, BC_SHAPE_LIST } from '../model/cn_beam_column_type';
import { cn_building } from '../model/cn_building';
import { cn_element } from '../model/cn_element';
import { cn_element_type } from '../model/cn_element_type';
import { code_to_label } from '../model/cn_opening_type';
import { cn_storey, STOREY_EXTERIOR_LABEL } from '../model/cn_storey';
import { cn_zone } from '../model/cn_zone';
import { cn_acos, cn_azimut, cn_clone, cn_dist, cn_normalize, cn_sub, cnx_dist } from './cn_utilities';
import { cn_plugin_option } from './cn_plugin_option';
import { cn_slab } from '../model/cn_slab';
import { cn_roof_slab } from '../model/cn_roof_slab';
import { CN_OUTER } from '../model/cn_wall';
import { logger } from './cn_logger';

// Constants to be used by upper level libraries, i.e. Caparol configurator
export const NOMENCLATURE_LABEL_FACING_NONE = 'Aucun'
export const NOMENCLATURE_COLUMNS_ID_FACING = 'facing'
export const NOMENCLATURE_COLUMNS_ID_FACING_ID = 'facing_ID'
export const NOMENCLATURE_COLUMNS_ID_FACING_SURFACE = 'facing_surface'
export const NOMENCLATURE_COLUMNS_ID_FACADE_AREA = 'facade_area'
export const NOMENCLATURE_COLUMNS_ID_FACADE_FULL_AREA = 'facade_full_area'
export const NOMENCLATURE_COLUMNS_ID_FACADE_JOINING_ANGLE = 'facade_joining_angle'
export const NOMENCLATURE_COLUMNS_ID_FACADE_BORDER_ANGLE = 'facade_border_angle'
export const NOMENCLATURE_COLUMNS_ID_FACADE_HIGH_LINE = 'facade_high_line'
export const NOMENCLATURE_COLUMNS_ID_FACADE_LOW_LINE = 'facade_low_line'
export const NOMENCLATURE_COLUMNS_ID_FACADE_EMBRASURE = 'facade_embrasure'
export const NOMENCLATURE_COLUMNS_ID_FACADE_OPENING_AREA = 'facade_opening_area'
export const NOMENCLATURE_COLUMNS_ID_FACADE_DOOR_LEDGE = 'facade_door_ledge'
export const NOMENCLATURE_COLUMNS_ID_FACADE_WINDOW_LEDGE = 'facade_window_ledge'

/**
 * @class cn_nomenclature
 * Juster a label and a list of values
 */
export class cn_nomenclature {
    /**
     * @param { string } label
     */
    constructor(label = '') {
        this.id = null;
        this.code_bim = null;
        /** Label including unit */
        this.label = label;
        /** Label without unit */
        this.raw_label = label;
        this.type = 'string';
        this.decimals = 0;
        this.visible = true;
        this.values = [];
        this.formatted_values = [];
        this.virtual = false;
    }

    /**
     * Sets attribute and return updated object
     * @param {string} id
     * @return {cn_nomenclature}
     */
    withId(id) {
        this.id = id;
        return this;
    }

    /**
     * Sets attribute and return updated object
     * @param {string} code_bim
     * @return {cn_nomenclature}
     */
    withCodeBim(code_bim) {
        this.code_bim = code_bim;
        return this;
    }

    /**
     * Sets number attributes and return updated object
     * @param {string} unit
     * @param {number} decimals
     * @return {cn_nomenclature}
     */
    withNumberDefinition(unit, decimals = 0) {
        this.type = 'number';
        this.unit = unit;
        this.decimals = decimals;
        if (unit) {
            this.label = this.raw_label + ` (${unit})`;
        }
        return this;
    }

    /**
     * Sets attribute and return updated object
     * @param { 'string' | 'number' | 'date' | 'boolean' } type
     * @return {cn_nomenclature}
     */
    withType(type) {
        this.type = type;
        return this;
    }

    /**
     * Sets attribute and return updated object
     * @param {boolean} visible
     * @return {cn_nomenclature}
     */
    withVisibility(visible) {
        this.visible = visible;
        return this;
    }

    /**
     * Sets attribute and return updated object
     * @param {boolean} virtual
     * @return {cn_nomenclature}
     */
    withVirtual(virtual) {
        this.virtual = virtual;
        return this;
    }

    /**
     * Returns csv export of a list of nomenclatures
     * @param {cn_nomenclature[]} list
     * @returns {string} csv string
     */
    static to_csv(list) {
        const nom_list = this.format_values(list);
        let csv = '';
        let size = 0;
        for (let n = 0; n < nom_list.length; n++) {
            if (n > 0) csv += ';';
            csv += nom_list[n].label;
            if (nom_list[n].formatted_values.length > size)
                size = nom_list[n].formatted_values.length;
        }
        csv += '\n';

        for (let l = 0; l < size; l++) {
            for (let n = 0; n < nom_list.length; n++) {
                if (n > 0) csv += ';';
                if (l >= nom_list[n].formatted_values.length) continue;
                const v = nom_list[n].formatted_values[l];
                csv += v;
            }
            csv += '\n';
        }
        return csv;
    }

    /**
     * Returns html of a list of nomenclatures
     * @param {cn_nomenclature[]} list
     * @returns {string} csv string
     */
    static to_html(list) {
        const nom_list = this.format_values(list);
        let size = 0;
        for (let n = 0; n < nom_list.length; n++) {
            if (nom_list[n].values.length > size)
                size = nom_list[n].values.length;
        }

        let html = '';
        html += '<table width=\'100%\'>';
        html += '<tr>';
        nom_list.forEach(column => {
            html += `<th>${column.label}</th>`;
        });
        html += '</tr>';
        html += '<tbody>';
        for (let l = 0; l < size; l++) {
            html += '<tr>';
            nom_list.forEach(column => {
                html += `<td>${(l < column.values.length) ? column.formatted_values[l] : ''}</td>`;
            });
            html += '</tr>';
        }
        html += '</tbody>';
        html += '</table>';
        return html;
    }

    /**
     * Format values by type
     *
     * @param {cn_nomenclature[]} list
     */
    static format_values(list) {
        return list.map(nomenclature => {
            nomenclature.formatted_values = nomenclature.values.map(value => {
                let result = null
                if (!nomenclature.virtual) {
                    if (value || value === 0 || value === false) {
                        if (nomenclature.type === 'string') {
                            result = value.toString();
                        } else if (nomenclature.type === 'number' && Number.isFinite(value)) {
                            result = value.toLocaleString('fr', { minimumFractionDigits: nomenclature.decimals || 0, maximumFractionDigits: nomenclature.decimals || 0 })
                        } else if (nomenclature.type === 'boolean') {
                            result = value ? 'Oui' : 'Non';
                        } else if (nomenclature.type === 'date') {
                            result = value.toLocaleDateString('fr');
                        } else {
                            result = value.toString();
                        }
                    } else {
                        result = '';
                    }
                } else {
                    result = value;
                }
                return result;
            })
            return nomenclature;
        })
    }
}

//***********************************************************************************
//***********************************************************************************
/**
 * Buils wall's types  nomenclatures for the building
 * @param {cn_building} building
 * @returns {cn_nomenclature[]}
 */
export function cn_nomenclature_walls_types(building) {
    const output = [];

    const wall_types = building.get_wall_types();
    const max_material = Math.max(...wall_types.map(it => it.layers.length));

    output.push(new cn_nomenclature('ID').withVirtual(true));
    output.push(new cn_nomenclature('ASSOCIATED_IDS').withVirtual(true));
    output.push(new cn_nomenclature('Nom'));
    output.push(new cn_nomenclature('Epaisseur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('U').withNumberDefinition('W/m².K', 3));
    output.push(new cn_nomenclature('Nombre d\'occurence').withNumberDefinition('', 0));
    output.push(new cn_nomenclature('Surface totale hors baies 1').withNumberDefinition('m²', 2));
    output.push(new cn_nomenclature('Surface totale hors baies 2').withNumberDefinition('m²', 2));
    output.push(new cn_nomenclature('Surface d\'échange').withNumberDefinition('m²', 2));

    for (let i = 1; i <= max_material; i++) {
        const nomenclatureName = new cn_nomenclature('Matériau ' + i);
        nomenclatureName.values = Array(wall_types.length).fill('');
        output.push(nomenclatureName);
        const nomenclatureThickness = new cn_nomenclature('Épaisseur matériau ' + i).withNumberDefinition('cm', 2);
        nomenclatureThickness.values = Array(wall_types.length).fill('');
        output.push(nomenclatureThickness);
    }

    const walls = building.storeys.flatMap(st => st.get_walls().map(wall => ({ wall, storey: st })));

    wall_types.forEach((wall_type, i) => {
        let nb = 0;
        let surface_hors_baie_1 = 0;
        let surface_hors_baie_2 = 0;
        let surface_echange = 0;
        let wall_interface_ids = [];
        walls.forEach(w => {
            w.wall.wall_interfaces.filter(wi => wi.has_wall_type(wall_type)).forEach(wi => {
                nb++;
                const wall_metrics = wi.get_metrics(w.storey);
                surface_hors_baie_1 += wall_metrics.gross_side_area_1;
                surface_hors_baie_2 += wall_metrics.gross_side_area_2;
                surface_echange += wall_metrics.gross_side_exchange_area;
                wall_interface_ids.push(wi.create_id_for_bbp(w.storey));
            });
        });


        let nomenclature_entry_index = 0;
        output[nomenclature_entry_index].values[i] = wall_type.ID;
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = wall_interface_ids;
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = wall_type.get_label();
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = wall_type.thickness;
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = wall_type.get_U();
        nomenclature_entry_index++;

        output[nomenclature_entry_index].values[i] = nb;
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = surface_hors_baie_1;
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = surface_hors_baie_2;
        nomenclature_entry_index++;
        output[nomenclature_entry_index].values[i] = surface_echange;
        nomenclature_entry_index++;

        wall_type.layers.forEach(layer => {
            output[nomenclature_entry_index].values[i] = layer.name;
            nomenclature_entry_index++;
            output[nomenclature_entry_index].values[i] = layer.thickness * 100;
            nomenclature_entry_index++;
        });
    });

    return output;
}

//***********************************************************************************
//***********************************************************************************
/**
 * Buils roof nomenclatures for the building
 * @param {cn_building} building
 * @returns {cn_nomenclature[]}
 */

export function cn_nomenclature_roofs(building) {
    const output = [];

    output.push(new cn_nomenclature('ID').withVirtual(true));
    output.push(new cn_nomenclature('Nom'));
    output.push(new cn_nomenclature('Type'));
    output.push(new cn_nomenclature('Niveau'));
    output.push(new cn_nomenclature('Index niveau').withVisibility(false));
    output.push(new cn_nomenclature('Structure'));
    output.push(new cn_nomenclature('Isolation'));
    output.push(new cn_nomenclature('Couverture'));
    output.push(new cn_nomenclature('Inclinaison').withNumberDefinition('°', 2));
    output.push(new cn_nomenclature('Orientation').withNumberDefinition('°', 0));
    output.push(new cn_nomenclature('Epaisseur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Surface').withNumberDefinition('m²', 2));
    output.push(new cn_nomenclature('U').withNumberDefinition('W/m².K', 3));
    output.push(new cn_nomenclature('Calques').withVisibility(false));
    if (cn_plugin_option.zpsos) {
        output.push(new cn_nomenclature('ZPSOs').withVisibility(false));
    }

    // for (var ns in building.storeys) {
    building.storeys.length && building.storeys.forEach((storey) => {
        if (storey.roof) {
            const roof = storey.roof;

            [...storey.roof.slabs, ...storey.roof.roof_dormers.filter(rd => rd.valid)].forEach(roof_element => {

                var slab = null;
                var roof_polygons = [];
                if (roof_element.constructor == cn_roof_slab) {
                    slab = roof_element;
                    roof_polygons = slab.build_3d_polygon(0, true, true).split();
                } else {
                    slab = roof_element.slab;
                    roof_polygons = roof_element.build_3d_polygons(0, CN_OUTER, true);
                }

                const slab_type = slab.slab_type;

                roof_polygons.forEach((polygon, index) => {
                    let k = 0;
                    //*** ID */
                    output[k].values.push(roof_element.create_id_for_bbp(storey, index));
                    k++

                    //*** name */
                    output[k].values.push(slab_type.get_label());
                    k++

                    //*** Type */
                    output[k].values.push(slab_type.get_label());
                    k++

                    //*** storey */
                    output[k].values.push(storey.get_storey_name());
                    k++
                    output[k].values.push(storey.get_storey_index_in_building());
                    k++

                    //*** Structure  */
                    output[k].values.push(slab_type.get_structure_label());
                    k++

                    //*** Insulation  */
                    output[k].values.push(slab_type.get_insulation_label());
                    k++

                    //*** Upper facing  */
                    output[k].values.push(slab_type.get_upper_facing_label());
                    k++

                    const normal = polygon.get_normal();

                    //*** Inclinaison  */
                    output[k].values.push(cn_acos(normal[2]) * 180 / Math.PI);
                    k++

                    //*** Orientation  */
                    let or = cn_azimut(normal, storey.building.compass_orientation);
                    output[k].values.push(or);
                    k++

                    //*** thickness  */
                    output[k].values.push(slab_type.thickness);
                    k++

                    //*** full area  */
                    output[k].values.push(polygon.get_area());
                    k++

                    //*** name */
                    output[k].values.push(slab_type.get_U());
                    k++

                    //*** layers & zpsos  */
                    k = export_layers_and_zpsos(storey, slab, slab_type, output, k);
                });
            });
        }
    });
    return output;
}

/**
 * Pushes zones nomenclatures headers
 * @param {any[]} output
 * @param {boolean} onlyMainZoneToolVisible
 * @param {boolean} addBbpZoneType
 */
export function push_cn_nomenclatures_zones(output, onlyMainZoneToolVisible, addBbpZoneType = false) {
    const default_zoning_type_tool = extension_instance.zone.get_default_zone_tool();
    extension_instance.zone.get_zones_tools().forEach(tool_zone => {
        const nomenclature_space = new cn_nomenclature(tool_zone.label_singular);
        if (onlyMainZoneToolVisible) {
            nomenclature_space.visible = tool_zone.property === default_zoning_type_tool.property;
        }
        output.push(nomenclature_space);
        if (addBbpZoneType && tool_zone.is_bbp_zone_type) {
            const nomenclature_space_type = new cn_nomenclature(`Type de ${tool_zone.label_singular.toLocaleLowerCase()}`);
            nomenclature_space_type.visible = nomenclature_space.visible;
            output.push(nomenclature_space_type);
        }
    })
}

//***********************************************************************************
//***********************************************************************************
/**
 * Builds beams nomenclatures for the building
 * @param {cn_building} building
 * @returns {cn_nomenclature[]}
 */

export function cn_nomenclature_beams(building) {
    const output = [];

    output.push(new cn_nomenclature('ID').withVirtual(true));
    output.push(new cn_nomenclature('Nom'));
    output.push(new cn_nomenclature('Identifiant'));
    output.push(new cn_nomenclature('Niveau'));
    output.push(new cn_nomenclature('Index niveau'));
    push_cn_nomenclatures_zones(output, true);
    output.push(new cn_nomenclature('Espace'));
    output.push(new cn_nomenclature('Matériau'));
    output.push(new cn_nomenclature('Type'));
    output.push(new cn_nomenclature('Orientation').withNumberDefinition('°', 0));
    output.push(new cn_nomenclature('Hauteur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Epaisseur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Largeur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Longueur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Calques').withVisibility(false));
    if (cn_plugin_option.zpsos) {
        output.push(new cn_nomenclature('ZPSOs').withVisibility(false));
    }

    building.storeys.length && building.storeys.forEach((storey) => {
        const zones = building.zones;
        const scene = storey.scene;
        /*** columns */
        scene.columns.length && scene.columns.forEach((column) => {
            populate_beam(output, zones, storey, column, 'Poteau');
        });
        /*** beams */
        scene.beams.length && scene.beams.forEach((beam) => {
            populate_beam(output, zones, storey, beam, 'Poutre');
        });
    });

    return output;
}

//***********************************************************************************
/**
 * Builds objects nomenclatures for the building with objects params
 * @param {cn_building} building
 * @param {{name: string, codeBim: string, nbDecimals: number, unit: string}[]} parameters_definitions
 * @param {string[]} dimensions_labels
 * @param is_exterior
 * @returns {cn_nomenclature[]}
 */

export function cn_nomenclature_objects(building, parameters_definitions, dimensions_labels = ['Largeur', 'Profondeur', 'Hauteur'], is_exterior = false) {

    const params_valued_codes_bim = [];

    // Récupère les colonnes correspondants aux paramètres de type valorisés
    building.objects.length && building.objects.forEach(obj => {
        if (obj.source && obj.source.parameters) {
            for (const [param_code_bim, param_value] of Object.entries(obj.source.parameters)) {
                if (param_value != null && param_value !== '') {
                    if (!params_valued_codes_bim.find(it => it === param_code_bim)) {
                        params_valued_codes_bim.push(param_code_bim);
                    }
                }
            }
        }
    });

    // Récupère les colonnes correspondants aux paramètres d'instances valorisés
    building.storeys.length && building.storeys.forEach(storey => {
        storey.scene.object_instances.length && storey.scene.object_instances.forEach(obj => {
            if (obj.parameters) {
                for (const [param_code_bim, param_value] of Object.entries(obj.parameters)) {
                    if (param_value != null && param_value !== '') {
                        if (!params_valued_codes_bim.find(it => it === param_code_bim)) {
                            params_valued_codes_bim.push(param_code_bim);
                        }
                    }
                }
            }
        });
    });

    const output = [];
    output.push(new cn_nomenclature('ID').withVirtual(true));
    output.push(new cn_nomenclature('ASSOCIATED_IDS').withVirtual(true));
    output.push(new cn_nomenclature('Catégorie'));
    output.push(new cn_nomenclature('Nom'));
    output.push(new cn_nomenclature('Identifiant'));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Niveau'));
        output.push(new cn_nomenclature('Index niveau').withVisibility(false));
    }
    push_cn_nomenclatures_zones(output, true);
    output.push(new cn_nomenclature('Espace'));
    output.push(new cn_nomenclature(dimensions_labels[0]).withNumberDefinition('m', 2));
    output.push(new cn_nomenclature(dimensions_labels[1]).withNumberDefinition('m', 2));
    output.push(new cn_nomenclature(dimensions_labels[2]).withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Orientation').withNumberDefinition('°', 0));

    if (params_valued_codes_bim.length > 0) {
        params_valued_codes_bim.forEach(code_bim => {
            const param_definition = parameters_definitions.find(t => t.codeBim === code_bim);
            if (param_definition) {
                let nomenclature = new cn_nomenclature(param_definition.name).withCodeBim(param_definition.codeBim);
                if (param_definition.unit || param_definition.nbDecimals) {
                    nomenclature = nomenclature.withNumberDefinition(param_definition.unit, param_definition.nbDecimals ?? 2)
                }
                output.push(nomenclature);
            } else {
                console.error(`Aucune définition de paramètre trouvée pour le code bim ${code_bim}`);
                output.push(new cn_nomenclature(code_bim).withCodeBim(code_bim));
            }
        });
    }

    output.push(new cn_nomenclature('Calques').withVisibility(false));
    if (cn_plugin_option.zpsos) {
        output.push(new cn_nomenclature('ZPSOs').withVisibility(false));
    }

    const zones = building.zones;
    if (is_exterior) {
        populate_nomenclature_objects(building.exterior, output, zones, params_valued_codes_bim);
    } else {
        building.storeys.length && building.storeys.forEach((storey) => {
            populate_nomenclature_objects(storey, output, zones, params_valued_codes_bim);
        });
    }

    return output;
}

/**
 * Builds beam or column nomenclatures for the given storey
 * @param {any[]} output
 * @param {{[property: string]: cn_zone[]}}zones
 * @param {cn_storey} storey
 * @param item (beam or column)
 * @param item_type
 */
function populate_beam(output, zones, storey, item, item_type) {
    const element_type = item.element_type;
    let k = 0;

    //*** ID */
    output[k].values.push(item.create_id_for_bbp(storey));
    k++

    //*** Type */
    output[k].values.push(`${item_type} ${element_type.get_label()}`);
    k++

    //*** Identifyer */
    output[k].values.push(item.get_readable_id(storey));
    k++

    //*** storey */
    output[k].values.push(storey.get_storey_name());
    k++
    output[k].values.push(storey.get_storey_index_in_building());
    k++

    const spaces = item.get_spaces(storey.scene);

    //*** zone */
    extension_instance.zone.get_zones_tools().forEach(tool_zone => {
        const zones_to_display = new Set();
        spaces.forEach(space => {
            const zone = find_zone(zones[tool_zone.property], space.ID, storey.ID);
            if (zone) {
                zones_to_display.add(zone);
            }
        })
        output[k].values.push(Array.from(zones_to_display).join(', '));
        k++;
    });

    //*** space */
    output[k].values.push(Array.from(spaces).map(space => space.get_name(storey)).join(', '));
    k++

    //*** material */
    output[k].values.push(code_to_label(element_type.material, BC_MATERIAL_LIST));
    k++

    //*** shape */
    output[k].values.push(code_to_label(element_type.shape, BC_SHAPE_LIST));
    k++

    //*** orientation */
    output[k].values.push(item.orientation);
    k++

    //*** height */
    if (item_type === 'Poteau') {
        output[k].values.push(item.height ? item.height : storey.height);
    } else {
        let heightA = item.vertices[0][2] === 0 ? storey.height : item.vertices[0][2];
        let heightB = item.vertices[1][2] === 0 ? storey.height : item.vertices[1][2];
        output[k].values.push(heightA <= heightB ? (heightA.toFixed(2) + ' / ' + heightB.toFixed(2)) : (heightB.toFixed(2) + ' / ' + heightA.toFixed(2)));
    }
    k++

    //*** thickness */
    output[k].values.push(item.element_type.thickness);
    k++

    //*** width */
    output[k].values.push(item.element_type.width);
    k++

    //*** length */
    let distance;
    if (item_type === 'Poteau') {
        distance = item.height === false ? storey.height : item.height
    } else {
        let a = item.vertices[0]
        let b = item.vertices[1]
        distance = Number((Math.sqrt(Math.pow(cn_dist(a, b), 2) + Math.pow(a[2] - b[2], 2))));
    }
    output[k].values.push(distance);
    k++

    //*** layers & zpsos  */
    k = export_layers_and_zpsos(storey, item, item_type, output, k);
}

/**
 * Builds beam or column nomenclatures for the given storey
 * @param {cn_storey} storey
 * @param {any[]} output
 * @param {{[property: string]: cn_zone[]}} zones
 * @param params_codes_bim
 */
function populate_nomenclature_objects(storey, output, zones, params_codes_bim) {

    const building = storey.building;

    const populate_nomenclature_object = (object_instance, is_roof) => {

        let k = 0;

        //*** ID */
        output[k].values.push(object_instance.create_id_for_bbp(storey));
        k++

        //*** ASSOCIATED ID */
        const associated_ids = [];
        if (object_instance.virtual) {
            associated_ids.push(object_instance.space.create_id_for_bbp(storey));
        }
        output[k].values.push(associated_ids);
        k++

        //*** Category */
        output[k].values.push(object_instance.object.get_product_category(building));
        k++

        //*** Name */
        output[k].values.push(object_instance.object.get_label());
        k++

        //*** Name */
        output[k].values.push(object_instance.get_readable_id(storey));
        k++

        //*** Storey */
        if (!storey.exterior) {
            output[k].values.push(storey.get_storey_name());
            k++
            output[k].values.push(storey.get_storey_index_in_building());
            k++
        }

        //*** Zone */
        extension_instance.zone.get_zones_tools().forEach(tool_zone => {
            let zone_to_display = '';
            if (object_instance.space) {
                zone_to_display = find_zone(zones[tool_zone.property], object_instance.space.ID, storey.ID);
            }
            output[k].values.push(zone_to_display);
            k++;
        });

        //*** Pièce */
        let space_name = '';
        if (is_roof) {
            space_name = 'Toit';
        } else if (object_instance.space) {
            space_name = object_instance.space.get_name(storey);
        }
        output[k].values.push(space_name);
        k++


        //*** Largeur */
        output[k].values.push(object_instance.object.size[1]);
        k++

        //*** Profondeur */
        output[k].values.push(object_instance.object.size[0]);
        k++

        //*** Hauteur */
        output[k].values.push(object_instance.object.size[2]);
        k++

        //*** Orientation */
        output[k].values.push(object_instance.orientation);
        k++

        //*** Parameters */
        const regex_date = '^(\\d{4})-(\\d\\d)-(\\d\\d)T(\\d\\d):(\\d\\d):(\\d\\d).(\\d\\d\\d)Z$';
        params_codes_bim.forEach(code_bim => {
            const value_from_instance = object_instance.parameters[code_bim];
            const value_from_type = object_instance.object.source.parameters[code_bim];
            let value;
            if (value_from_instance != null && value_from_instance !== '') {
                value = value_from_instance;
            } else if (value_from_type != null && value_from_type !== '') {
                value = value_from_type;
            } else {
                value = '';
            }
            if (value && isNaN(value) && value.match(regex_date)) {
                value = new Date(value).toLocaleDateString('fr');
            }
            output[k].values.push(value)
            k++
        })

        //*** layers & zpsos  */
        k = export_layers_and_zpsos(storey, object_instance, object_instance.object, output, k);

    };

    if (storey.scene.object_instances && storey.scene.object_instances.length) {
        storey.scene.object_instances.forEach(it => populate_nomenclature_object(it, false));
    }
    if (storey.roof && storey.roof.object_instances && storey.roof.object_instances.length) {
        storey.roof.object_instances.forEach(it => populate_nomenclature_object(it, true))
    }
}

/**
 * Find a zone associated to a space+storey
 * @param {cn_zone[]} zones
 * @param space_ID
 * @param storey_ID
 * @returns {string}
 */
export function find_zone(zones, space_ID, storey_ID) {
    let zone_to_display = '';
    zones && zones.length && zones.some((zone) => {
        const room = zone.rooms.length && zone.rooms.find((r) => {
            return r.space === space_ID && r.storey === storey_ID;
        });
        if (room) {
            return zone_to_display = zone.name;
        }
    });
    return zone_to_display;
}

/**
 *
 * @param {cn_storey} storey
 * @param {cn_element} element
 * @param {cn_element_type} element_type
 * @param {any[]} output
 * @param {number} k
 * @return {number} k updated
 */
export function export_layers_and_zpsos(storey, element, element_type, output, k) {

    const layers = storey.building.layers.filter(layer =>
        layer.elements.some(el => el.obj === element.ID && el.storey === storey.ID) || layer.elements_types.includes(element_type.ID)
    )
    output[k].values.push(layers.map(it => it.name).join(', '));
    k++;

    if (cn_plugin_option.zpsos) {
        const zpsos = storey.building.zpsos.filter(layer =>
            layer.elements.some(el => el.obj === element.ID && el.storey === storey.ID) || layer.elements_types.includes(element_type.ID)
        )
        output[k].values.push(zpsos.map(it => it.name).join(', '));
        k++;
    }

    return k;
}
